// The ROGO Default Report has no Customize —: it protects ROGO's brand/reliability, so that report
// is always the methodology ROGO stands behind.
import { useState } from "react";
import { X } from "lucide-react";
import {
  defaultSection180Options,
  type Section180ExtraNutrient,
  type Section180Options,
} from "./section180";
import {
  DEFAULT_NUTRIENTS,
  LIME_KEY,
  nutrientUnit,
  type OfferedNutrient,
} from "./section180Nutrients";
import { Modal } from "../../ui/Modal";
import {
  capCls,
  groupCls,
  inputCls,
  numRow,
  priceRows,
  rowCls,
  stateLabel,
} from "./Section180PriceFields";

const textCls =
  "w-40 rounded border border-line bg-canvas px-2 py-1 text-[12px] text-fg outline-none focus:border-accent";

/*
 *
 * " is not a number); blank/garbage falls back to the default on confirm rather than silently
 * sending NaN into the arithmetic. Emptiness is tested on the STRIPPED string: "$" or "," alone
 * stripped to "" and `Number('')` is 0 — a lone punctuation keystroke became a ZERO threshold
 * (every sample "excess") or a $0 price.
 */
const num = (draft: string, fallback: number): number => {
  const stripped = draft.replace(/[$,\s]/g, "");
  if (stripped === "") return fallback;
  const v = Number(stripped);
  return Number.isFinite(v) && v >= 0 ? v : fallback;
};
/* * A user-supplied figure with NO platform default: it must parse to a real, non-negative number. */
const supplied = (draft: string): number | null => {
  const stripped = draft.replace(/[$,\s]/g, "");
  if (stripped === "") return null;
  const v = Number(stripped);
  return Number.isFinite(v) && v >= 0 ? v : null;
};

interface ExtraDraft {
  product: string;
  threshold: string;
  lbsElementalPerPpm: string;
  lbsNutrientPerLbElemental: string;
  lbsNutrientPerTonProduct: string;
  /* * LIME only — the trade quotes lime in tons per 0.1 of pH build, not through the lbs chain */
  tonsPerTenthPh: string;
  prices: Record<string, string>;
}
const emptyDraft = (states: string[]): ExtraDraft => ({
  product: "",
  threshold: "",
  lbsElementalPerPpm: "",
  lbsNutrientPerLbElemental: "",
  lbsNutrientPerTonProduct: "",
  tonsPerTenthPh: "",
  prices: Object.fromEntries(states.map((st) => [st, ""])),
});

export function Section180CustomizeModal({
  states,
  available,
  initial,
  onCancel,
  onConfirm,
}: {
  states: string[];
  /* * every nutrient the selection's samples carry — P and K always lead */
  available?: OfferedNutrient[];
  initial?: Section180Options;
  onCancel: () => void;
  onConfirm: (options: Section180Options) => void;
}) {
  const base = initial ?? defaultSection180Options(states);
  const offered = available ?? [];

  const [pThr, setPThr] = useState(String(base.pThreshold));
  const [kLow, setKLow] = useState(String(base.kThresholdLowCec));
  const [kHigh, setKHigh] = useState(String(base.kThresholdHighCec));
  const [mapPrices, setMapPrices] = useState<Record<string, string>>(
    Object.fromEntries(
      states.map((st) => [
        st,
        String(
          base.prices[st]?.map ??
            defaultSection180Options([st]).prices[st]!.map,
        ),
      ]),
    ),
  );
  const [potashPrices, setPotashPrices] = useState<Record<string, string>>(
    Object.fromEntries(
      states.map((st) => [
        st,
        String(
          base.prices[st]?.potash ??
            defaultSection180Options([st]).prices[st]!.potash,
        ),
      ]),
    ),
  );
  const [valueAsOf, setValueAsOf] = useState(
    base.valueAsOf ?? new Date().toISOString().slice(0, 10),
  );
  const [included, setIncluded] = useState<string[]>(
    (base.extras ?? []).map((x) => x.key),
  );
  const [drafts, setDrafts] = useState<Record<string, ExtraDraft>>(() =>
    Object.fromEntries(
      (base.extras ?? []).map((x) => [
        x.key,
        {
          product: x.product,
          threshold: String(x.threshold),
          lbsElementalPerPpm:
            x.lbsElementalPerPpm != null ? String(x.lbsElementalPerPpm) : "",
          lbsNutrientPerLbElemental:
            x.lbsNutrientPerLbElemental != null
              ? String(x.lbsNutrientPerLbElemental)
              : "",
          lbsNutrientPerTonProduct:
            x.lbsNutrientPerTonProduct != null
              ? String(x.lbsNutrientPerTonProduct)
              : "",
          tonsPerTenthPh:
            x.tonsPerTenthPh != null ? String(x.tonsPerTenthPh) : "",
          prices: Object.fromEntries(
            states.map((st) => [st, String(x.prices[st] ?? "")]),
          ),
        },
      ]),
    ),
  );

  const draftOf = (key: string): ExtraDraft =>
    drafts[key] ?? emptyDraft(states);
  const setDraft = (key: string, patch: Partial<ExtraDraft>) =>
    setDrafts((d) => ({ ...d, [key]: { ...draftOf(key), ...patch } }));

  const toggle = (key: string) =>
    setIncluded((inc) => {
      if (inc.includes(key)) return inc.filter((k) => k !== key);
      if (!drafts[key]) setDrafts((d) => ({ ...d, [key]: emptyDraft(states) }));
      return [...inc, key];
    });

  // an added nutrient is only claimable once EVERY figure behind its numbers is supplied
  const missing = included.flatMap((key) => {
    const d = draftOf(key);
    const label = offered.find((o) => o.key === key)?.label ?? key;
    const gaps: string[] = [];
    if (supplied(d.threshold) == null) gaps.push("threshold");
    if (key === LIME_KEY) {
      if (!supplied(d.tonsPerTenthPh)) gaps.push("tons per 0.1 pH build");
    } else {
      if (!supplied(d.lbsElementalPerPpm)) gaps.push("lbs elemental per ppm");
      if (!supplied(d.lbsNutrientPerLbElemental))
        gaps.push("lbs nutrient per lb elemental");
      if (!supplied(d.lbsNutrientPerTonProduct))
        gaps.push("lbs nutrient per ton");
    }
    if (states.some((st) => supplied(d.prices[st] ?? "") == null))
      gaps.push("market value");
    return gaps.length ? [`${label}: ${gaps.join(", ")}`] : [];
  });

  const confirm = () => {
    if (missing.length) return;
    const def = defaultSection180Options(states);
    const extras: Section180ExtraNutrient[] = included.map((key) => {
      const d = draftOf(key);
      const label = offered.find((o) => o.key === key)?.label ?? key;
      const common = {
        key,
        label,
        product: d.product.trim() || label,
        threshold: supplied(d.threshold) ?? 0,
        prices: Object.fromEntries(
          states.map((st) => [st, supplied(d.prices[st] ?? "") ?? 0]),
        ),
      };
      // lime carries ONLY its tons-per-0.1-pH factor; the lbs chain is absent, not zero-filled
      return key === LIME_KEY
        ? { ...common, tonsPerTenthPh: supplied(d.tonsPerTenthPh) ?? 0 }
        : {
            ...common,
            lbsElementalPerPpm: supplied(d.lbsElementalPerPpm) ?? 0,
            lbsNutrientPerLbElemental:
              supplied(d.lbsNutrientPerLbElemental) ?? 0,
            lbsNutrientPerTonProduct: supplied(d.lbsNutrientPerTonProduct) ?? 0,
          };
    });
    onConfirm({
      pThreshold: num(pThr, def.pThreshold),
      kThresholdLowCec: num(kLow, def.kThresholdLowCec),
      kThresholdHighCec: num(kHigh, def.kThresholdHighCec),
      prices: Object.fromEntries(
        states.map((st) => [
          st,
          {
            map: num(mapPrices[st] ?? "", def.prices[st]!.map),
            potash: num(potashPrices[st] ?? "", def.prices[st]!.potash),
          },
        ]),
      ),
      extras,
      valueAsOf,
    });
  };

  // Backdrop dismiss fires only when the gesture BEGAN on the backdrop: a click event is dispatched
  // on the common ancestor of mousedown/mouseup, so drag-selecting a.
  const [downOnBackdrop, setDownOnBackdrop] = useState(false);
  return (
    <Modal>
      <div
        className="fixed inset-0 z-dialog flex items-center justify-center bg-black/50 p-4"
        onMouseDown={(e) => setDownOnBackdrop(e.target === e.currentTarget)}
        onClick={(e) => {
          if (e.target === e.currentTarget && downOnBackdrop) onCancel();
        }}
      >
        <div
          className="max-h-[88vh] w-[560px] overflow-auto rounded-lg border border-line bg-surface p-4 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[14px] font-semibold text-fg">
              Customize the report
            </h2>
            <button
              className="rounded p-1 text-muted hover:bg-hover hover:text-fg"
              onClick={onCancel}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          <div className="flex flex-col gap-3">
            <div className={groupCls}>
              <span className={capCls}>Nutrients to include</span>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {offered.map((n) => {
                  const locked = DEFAULT_NUTRIENTS.includes(n.key);
                  return (
                    <label
                      key={n.key}
                      className={`flex items-center gap-1.5 ${locked ? "opacity-70" : "cursor-pointer"}`}
                    >
                      <input
                        type="checkbox"
                        className="accent-[var(--color-accent)]"
                        checked={locked || included.includes(n.key)}
                        disabled={locked}
                        onChange={() => toggle(n.key)}
                        aria-label={n.label}
                      />
                      <span className="text-[12px] text-fg">{n.label}</span>
                    </label>
                  );
                })}
              </div>
              <span className="mt-1 block text-[10px] text-faint">
                P and K are always included — they are the claim ROGO stands
                behind. Anything you add needs its own threshold and conversions
                below.
              </span>
            </div>

            <div className={groupCls}>
              <span className={capCls}>Build thresholds (ppm)</span>
              {numRow("P Build Threshold", pThr, setPThr)}
              {numRow("K Build Threshold (Under 5 CEC)", kLow, setKLow)}
              {numRow("K Build Threshold (Over 5 CEC)", kHigh, setKHigh)}
              <span className="block text-[10px] text-faint">
                A CEC of exactly 5 uses the higher threshold.
              </span>
            </div>

            <div className={groupCls}>
              <span className={capCls}>Market values as of</span>
              <div className={rowCls}>
                <span className="text-[12px] text-fg">Value as of date</span>
                <input
                  type="date"
                  className={textCls}
                  value={valueAsOf}
                  onChange={(e) => setValueAsOf(e.target.value)}
                  aria-label="Value as of date"
                />
              </div>
              <span className="block text-[10px] text-faint">
                The date the letter quotes its market values for — set it back
                to file for a past purchase.
              </span>
            </div>

            {priceRows(
              states,
              "MAP Market Value/Ton (State)",
              mapPrices,
              setMapPrices,
            )}
            {priceRows(
              states,
              "Potash Market Value/Ton (State)",
              potashPrices,
              setPotashPrices,
            )}

            {included.map((key) => {
              const n = offered.find((o) => o.key === key);
              const d = draftOf(key);
              const unit = nutrientUnit(key);
              return (
                <div key={key} className={groupCls}>
                  <span className={capCls}>
                    {n?.label ?? key} — your figures
                  </span>
                  <div className={rowCls}>
                    <span className="text-[12px] text-fg">Product name</span>
                    <input
                      className={textCls}
                      value={d.product}
                      onChange={(e) =>
                        setDraft(key, { product: e.target.value })
                      }
                      placeholder={n?.label ?? key}
                      aria-label={`${n?.label ?? key} product name`}
                    />
                  </div>
                  {numRow(
                    `${n?.label ?? key} Build Threshold (${unit})`,
                    d.threshold,
                    (v) => setDraft(key, { threshold: v }),
                  )}
                  {key === LIME_KEY ? (
                    numRow(
                      "Tons of Lime / 0.1 BpH-pH Build",
                      d.tonsPerTenthPh,
                      (v) => setDraft(key, { tonsPerTenthPh: v }),
                    )
                  ) : (
                    <>
                      {numRow(
                        "Lbs Elemental per 1 PPM Increase",
                        d.lbsElementalPerPpm,
                        (v) => setDraft(key, { lbsElementalPerPpm: v }),
                      )}
                      {numRow(
                        "Lbs Nutrient per 1 Lb Elemental",
                        d.lbsNutrientPerLbElemental,
                        (v) => setDraft(key, { lbsNutrientPerLbElemental: v }),
                      )}
                      {numRow(
                        "Lbs Nutrient per Ton Product",
                        d.lbsNutrientPerTonProduct,
                        (v) => setDraft(key, { lbsNutrientPerTonProduct: v }),
                      )}
                    </>
                  )}
                  {states.map((st) => (
                    <div key={st} className={rowCls}>
                      <span className="text-[12px] text-fg">
                        Market Value/Ton — {stateLabel(st)}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="text-[12px] text-muted">$</span>
                        <input
                          className={inputCls}
                          value={d.prices[st] ?? ""}
                          onChange={(e) =>
                            setDraft(key, {
                              prices: { ...d.prices, [st]: e.target.value },
                            })
                          }
                          aria-label={`${n?.label ?? key} Market Value/Ton ${st}`}
                          inputMode="decimal"
                        />
                      </span>
                    </div>
                  ))}
                  {key === LIME_KEY && (
                    <span className="block text-[10px] text-faint">
                      Lime reads each sample&rsquo;s buffer pH when it has one,
                      otherwise its pH — and is claimed in TONS, straight from
                      the pH build, so it needs no elemental conversion.
                    </span>
                  )}
                </div>
              );
            })}

            {missing.length > 0 && (
              <p className="text-[10px] leading-snug text-amber-500">
                Still needed before this report can be built:{" "}
                {missing.join(" · ")}.
              </p>
            )}

            <div className="flex items-center justify-end gap-2">
              <button
                className="rounded border border-line px-3 py-1.5 text-[12px] text-fg hover:bg-hover"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                disabled={missing.length > 0}
                className="rounded border border-accent bg-accent px-3 py-1.5 text-[12px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
                onClick={confirm}
              >
                Download report
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
